import {Chip} from "@mui/material";
import {AxiosResponse } from "axios";
import React from "react";
import {NavLink} from "react-router-dom";
import Button from "../../../../common/components/shared/button/button";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import Loader from "../../../../common/components/shared/loader/loader";
import ReloadButton from "../../../../common/components/shared/reloadButton/reloadButton";
import RemovePopup from "../../../../common/components/shared/removePopup/removePopup";
import Table, {TableActionFunction} from "../../../../common/components/shared/table/table";
import {HttpErrorModel} from "../../../../common/models/httpError";
import { IPaginationStore } from "../../../../common/stores/paginationStore";
import {storesContext} from "../../../../common/stores/stores";
import {Api} from "../../../../helpers/api";
import {routingAddresses} from "../../../../helpers/routing";
import i18n from "../../../../i18n/i18n";
import {RouteModel} from "../../../models/route";
import {RouteService} from "../../../services/routeService";
import "../../topology.scss";
import ActiveOrInactivePopup from "./activeOrInactivePopup/activeOrInactivePopup";
import "./routeList.scss";

enum TypeOfPopup {
    activePopup = "activePopup",
    inactivePopup = "inactivePopup",
    removePopup = "removePopup"
}

interface RouteListState {
    routeData: RouteModel[] | undefined | null;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    currentPage: number;
    isTableLoader: boolean;
    selectedElement: RouteModel | undefined;
    displayPopup: TypeOfPopup | null;
    activeRouteResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    inactiveRouteResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    removeRouteResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    isPopupActionWaiting: boolean;
}

export default class RouteList extends React.Component<any, RouteListState>  {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            routeData: undefined,
            response: undefined,
            currentPage: 0,
            isTableLoader: false,
            selectedElement: undefined,
            displayPopup: null,
            activeRouteResponse: undefined,
            inactiveRouteResponse: undefined,
            removeRouteResponse: undefined,
            isPopupActionWaiting: false
        };
    }

    get routeData() {
        return this.state.routeData;
    }

    public async componentDidMount() {
        await this.getAllRoutes();
    }

    public render(): JSX.Element {
        const properties = ["isActive", "name", "supportedRelationTypes", "supportedCargoTypes", "wayPoints"];
        const headers = properties.map(key => i18n.t("topology.routes.headers." + key));
        const actions = this.renderActions;

        return (
            <div className="routes">
                {this.renderWrapperContent(headers, properties, "routes", actions, this.renderCell.bind(this))}
            </div>
        );
    }

    protected renderWrapperContent(headers: string[], properties: string[], testId: string, actions?: TableActionFunction, renderCell?: (row: any, property: string) => JSX.Element) {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={i18n.t("topology.routes.errors.retrievalErrorSuffix")}
                />
            );
        } else {
            if (this.routeData === undefined) {
                return <Loader/>;
            } else {
                return (
                    <>
                        {this.renderHeader()}
                        <div className="route-content">
                            {this.routeData && this.routeData!.length && this.routeData!.length > 0 ? (
                                <Table
                                    headers={headers}
                                    rows={this.state.routeData!}
                                    properties={properties}
                                    testId={`${testId}-table`}
                                    actions={actions ? actions.bind(this) : actions}
                                    renderCell={renderCell}
                                    pageable={true}
                                    currentPage={this.state.currentPage}
                                    isOldPagination={false}
                                    isTableLoader={this.state.isTableLoader}
                                    onPageChange={this.getAllRoutes.bind(this)}
                                />
                            ) : (
                                <Table
                                    headers={headers}
                                    rows={[]}
                                    properties={properties}
                                    testId={`${testId}-table`}
                                    currentPage={this.state.currentPage}
                                    numberOfElements={0}
                                />
                            )}
                            {this.renderPopupsForModifyRoute()}
                            {this.renderRemovePopup()}
                        </div>
                    </>
                );
            }
        }
    }

    private renderHeader() {
        return (
            <div className="topology-header">
                <NavLink to={routingAddresses.topology.routes.add} data-testid="add-route">
                    <Button type="primary" text={i18n.t("topology.routes.actions.addRoute.button")}/>
                </NavLink>
                <ReloadButton
                    reloadFunc={this.reload.bind(this)}
                    testId="route-list-reload-btn"
                />
            </div>
        );
    }

    private renderCell(row: any, property: string): JSX.Element {
        switch (property) {
            case "isActive":
                return <Chip className={row[property] ? "active" : "inactive"} label={i18n.t(`activeStatus.${row[property] ? "ACTIVE" : "INACTIVE"}`)}/>;
            case "supportedRelationTypes":
            case "supportedCargoTypes":
            case "wayPoints":
                return <>{this.isCommaSeparatedList(row, property)}</>;
            default:
                return row[property];
        }
    }

    private renderActions(element: RouteModel) {
        // @ts-ignore
        const elementId = element.id;

        return (
            <>
                {elementId && (
                    <>
                        <NavLink
                            to={`${routingAddresses.topology.routes.modify}/${elementId}`}
                            className="modify-button"
                            data-testid={`modify-route-${elementId}`}
                        >
                            {i18n.t("topology.routes.actions.modifyRoute.button")}
                        </NavLink>
                        {this.renderActiveOrInactiveActions(element)}
                        {this.renderRemoveActions(element)}
                    </>
                )}
            </>
        );
    }

    private renderInactiveButton(route: RouteModel) {
        return (
            <span className="inactive-button" data-testid={`inactive-button-${route.id}`} onClick={() => {
                this.setState({selectedElement: route});
                this.onRouteActions(TypeOfPopup.inactivePopup);
            }}>
                {i18n.t("topology.routes.actions.inactiveAction")}
            </span>
        );
    }

    private renderActiveButton(route: RouteModel) {
        return (
            <span className="active-button" data-testid={`active-button-${route.id}`} onClick={() => {
                this.setState({selectedElement: route});
                this.onRouteActions(TypeOfPopup.activePopup);
            }}>
                {i18n.t("topology.routes.actions.activeAction")}
            </span>
        );
    }

    private renderActiveOrInactiveActions(route: RouteModel) {
        return route.isActive ? this.renderInactiveButton(route) : this.renderActiveButton(route);
    }

    private renderRemoveActions(route: RouteModel) {
        return (
            <span className="remove-button" data-testid={`remove-button-${route.id}`} onClick={() => {
                this.setState({selectedElement: route});
                this.onRouteActions(TypeOfPopup.removePopup);
            }}>
                {i18n.t("topology.routes.actions.removeAction")}
            </span>
        );
    }

    private onRouteActions(action: TypeOfPopup) {
        this.setState({displayPopup: action});
    }

    private renderPopupsForModifyRoute(){
        return (
            <>
                <ActiveOrInactivePopup
                    action="active"
                    displayPopup={this.state.displayPopup === TypeOfPopup.activePopup}
                    closePopup={this.closePopup.bind(this)}
                    confirm={this.confirmActiveOrInactive.bind(this, true)}
                    routeName={this.state.selectedElement?.name || ""}
                    response={this.state.activeRouteResponse}
                    isWaiting={this.state.isPopupActionWaiting}
                />
                <ActiveOrInactivePopup
                    action="inactive"
                    displayPopup={this.state.displayPopup === TypeOfPopup.inactivePopup}
                    closePopup={this.closePopup.bind(this)}
                    confirm={this.confirmActiveOrInactive.bind(this, false)}
                    routeName={this.state.selectedElement?.name || ""}
                    response={this.state.inactiveRouteResponse}
                    isWaiting={this.state.isPopupActionWaiting}
                />
            </>
        );
    }

    private renderRemovePopup(){
        const content = (
            <div>
                {i18n.t("topology.routes.actions.removePopup.contentPart1")}
                <b>{this.state.selectedElement?.name}</b>
                {i18n.t("topology.routes.actions.removePopup.contentPart2")}
            </div>
        );

        return (
            <RemovePopup
                displayPopup={this.state.displayPopup === TypeOfPopup.removePopup}
                header={i18n.t("topology.routes.actions.removePopup.header")}
                secondHeader={i18n.t("topology.routes.actions.removePopup.secondHeader")}
                content={content}
                actionText={i18n.t("topology.routes.actions.removePopup.actionText")}
                closePopup={this.closePopup.bind(this)}
                confirm={this.confirmRemove.bind(this)}
                response={this.state.removeRouteResponse}
                isWaiting={this.state.isPopupActionWaiting}
                httpErrorMessage={i18n.t("topology.routes.actions.removePopup.errorSuffix")}
            />
        );
    }

    private closePopup() {
        this.setState({
            displayPopup: null,
            activeRouteResponse: undefined,
            inactiveRouteResponse: undefined,
            removeRouteResponse: undefined
        });
    }

    private async confirmRemove() {
        this.setState({removeRouteResponse: undefined, isPopupActionWaiting: true});

        const response = await RouteService.removeRoute(this.state.selectedElement!.id);

        this.setState({removeRouteResponse: response});

        if (Api.isSuccess(response)) {
            await this.getAllRoutes();
            this.closePopup();
        }

        this.setState({isPopupActionWaiting: false});
    }

    private async confirmActiveOrInactive(active: boolean) {
        this.setState({
            activeRouteResponse: undefined,
            inactiveRouteResponse: undefined,
            response: undefined,
            isPopupActionWaiting: true
        });

        const response = await RouteService.activeOrInactiveRoute(this.state.selectedElement!.id, active);

        if (active) {
            this.setState({activeRouteResponse: response});
        } else {
            this.setState({inactiveRouteResponse: response});
        }

        if (Api.isSuccess(response)) {
            await this.getAllRoutes();
            this.closePopup();
        }

        this.setState({isPopupActionWaiting: false});
    }

    private async getAllRoutes() {
        const paginationStore = this.context.paginationStore as IPaginationStore;

        this.setState({isTableLoader: true});

        const response = await RouteService.getAll({
            offset: paginationStore.offset,
            size: paginationStore.size
        });

        this.setState({
            routeData: response?.data?.data || null,
            response,
            currentPage: response?.data?.currentPage,
            isTableLoader: false,
            selectedElement: undefined
        });

        paginationStore.setNumberOfElements(response?.data?.numberOfElements);
        paginationStore.setNumberOfPages(response?.data?.numberOfPages);
    }

    private async reload() {
        this.setState({routeData: undefined});
        await this.getAllRoutes();
    }

    private isCommaSeparatedList(row: any, property: string): string {
        const result = [];

        if (row && property && row[property] && row[property].length) {
            for(let i = 0; i < row[property].length; i++) {
                if (property === "supportedRelationTypes") {
                    result.push(i18n.t(`relationType.${row[property][i]}`));
                } else if (property === "supportedCargoTypes") {
                    result.push(i18n.t(`cargoType.${row[property][i]}`));
                } else if (property === "wayPoints") {
                    result.push(row[property][i].location.name);
                }
            }
        }

        return result.join("; ");
    }
}
